import React,{ useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence} from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Building, 
  Calendar, 
  ChevronRight,
  Star, 
  User, 
  Users, 
  TrendingUp, 
  Sun, 
  Home,
  MapPin,
  ArrowUpRight,
  Globe,Diamond,
  Circle,
  Triangle,Hexagon,
  Award,
  Clock,
  Check,
  ArrowRight
} from 'lucide-react';

const AboutWidgetPremium = () => {
  useEffect(() => {
    // Import and initialize VanillaTilt
    const VanillaTilt = require('vanilla-tilt');
    
    VanillaTilt.init(document.querySelectorAll(".tilt-card"), {
      max: 15,
      speed: 400,
      glare: true,
      "max-glare": 0.2,
      scale: 1.03,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <HeroSection />
      
      {/* Our Story Section */}
      <StorySection />
      
      {/* Timeline Section */}
      <TimelineSection />
    </div>
  );
};

// Hero Section Component with New Color Scheme
const HeroSection = () => {
  return (
    <section className="relative overflow-hidden">
      {/* Background Gradient - New color scheme */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-amber-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800"></div>
      
      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-amber-200 dark:bg-amber-600/30 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-60 animate-blob"></div>
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-blue-100 dark:bg-blue-600/30 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-60 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-20 right-1/3 w-64 h-64 bg-emerald-100 dark:bg-emerald-600/30 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-60 animate-blob animation-delay-4000"></div>
      
      <div className="container mx-auto px-4 py-24 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Text Content */}
          <motion.div 
            className="lg:w-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full font-medium text-sm mb-6">
              <Building size={16} className="mr-2" />
              <span>Est. 2005</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-800 dark:text-gray-100 mb-6 leading-tight">
              Dev Bhoomi <span className="text-blue-600 dark:text-blue-400">Group</span>
            </h1>
            <div className="w-20 h-1 bg-blue-600 dark:bg-blue-500 mb-6"></div>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Founded by Mr. Rajinder Vashista in 2005, the Dev Bhoomi Group Companies is headquartered in Himachal Pradesh and consists of seven companies across four verticals.
            </p>
            <div className="flex flex-wrap gap-4">
              <motion.a 
                href="#story" 
                className="inline-flex items-center px-6 py-3 bg-blue-600 dark:bg-blue-700 text-white rounded-lg shadow-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Our Story</span>
                <ArrowUpRight size={18} className="ml-2" />
              </motion.a>
              <motion.a 
                href="#contact" 
                className="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg shadow-sm hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Contact Us</span>
                <ChevronRight size={18} className="ml-2" />
              </motion.a>
            </div>
          </motion.div>
          
          {/* Stats Card */}
          <motion.div 
            className="lg:w-1/2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-blue-100 dark:border-blue-900/50 relative tilt-card overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/20 bg-opacity-50 z-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23059669' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-2xl font-bold text-blue-800 dark:text-blue-300">Company Overview</h3>
                  <div className="flex items-center space-x-1 text-amber-500 dark:text-amber-400">
                    <Star size={16} fill="currentColor" />
                    <Star size={16} fill="currentColor" />
                    <Star size={16} fill="currentColor" />
                    <Star size={16} fill="currentColor" />
                    <Star size={16} fill="currentColor" />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-8 mb-10">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                      <Calendar size={24} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Founded</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">2005</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0">
                      <Building size={24} className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Companies</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">7 Entities</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Headquarters</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">Himachal Pradesh</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                      <Home size={24} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Real Estate Experience</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">20+ Years</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <User size={20} />
                    <span>Rajinder Vashista, Founder</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Story Section with Framer Motion Animation
const StorySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px 0px" });
  
  return (
    <section id="story" className="py-24 bg-white dark:bg-gray-800 relative overflow-hidden">
      {/* Decorative Circles */}
      <div className="absolute -right-20 top-0 w-80 h-80 rounded-full bg-emerald-50 dark:bg-emerald-900/20 z-0"></div>
      <div className="absolute -left-20 bottom-0 w-80 h-80 rounded-full bg-amber-50 dark:bg-amber-900/20 z-0"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Founder Photo */}
          <motion.div 
            className="lg:w-2/5"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
            ref={ref}
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-blue-600 dark:border-blue-500 rounded-xl z-0"></div>
              
              <div className="tilt-card relative z-10 bg-gradient-to-br from-blue-600 to-teal-800 dark:from-blue-700 dark:to-teal-900 rounded-xl overflow-hidden">
                <div className="aspect-[3/4] w-full relative">
                  {/* Actual Founder Image */}
                  <img
                    src="https://ik.imagekit.io/devhm/Rajendera%20Vashista.jpg.jpeg%20(1).jpg?updatedAt=1745934923986"
                    alt="Rajinder Vashista - Founder"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>

                {/* Experience Badge */}
                <div className="absolute top-4 right-4 bg-white dark:bg-gray-800 text-blue-800 dark:text-blue-300 rounded-full px-4 py-2 font-bold text-sm shadow-lg">
                  Since 2005
                </div>
              </div>
            </div>
          </motion.div>

          {/* Story Content */}
          <motion.div 
            className="lg:w-3/5"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-gray-100">The <span className="text-blue-600 dark:text-blue-400">Visionary</span> Journey</h2>
              <div className="w-20 h-1 bg-blue-600 dark:bg-blue-500"></div>
              
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Rajinder Vashista, in 2005, began his entrepreneurial venture as an Iron Rods Trader, and he realised it was not the time to rest on his laurels; instead, he made an informed decision to enter the Automobiles business in 2011 with a SKODA authorized dealership in Mandi (Himachal Pradesh) and Solan (Himachal Pradesh) in the year 2021.
              </p>
              
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                The highly progressive Dev Bhoomi group Companies places a significant emphasis on expansion and bringing prosperity to the state. In 2014, Dev Bhoomi Group Companies became the authorised dealer of Hyundai Motors India Ltd and since then it has marked a presence with various showrooms and workshops located in Mandi, Joginder Nagar, Ghumarwin, Bilaspur, Shimla, Karsog, Rampur, Rohru by the name of Dev Bhoomi Hyundai and is all set to expand over Himachal.
              </p>
              
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-100 dark:border-amber-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-3">Our Mission</h3>
                <p className="text-gray-700 dark:text-gray-300 italic">
                  "Under the progressive leadership of Rajinder Vashista, Dev Bhoomi Group Companies puts keen emphasis on customer satisfaction by making sure that each meeting with the customers is able to incarnate an impact that creates a greater value for both the business and the brand name of the Dev Bhoomi Group Companies."
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Completely Redesigned Timeline Section
const TimelineSection = () => {
    const controls = useAnimation();
    const [activeIndex, setActiveIndex] = useState(null);
    const [viewRef, isInView] = useInView({ 
      threshold: 0.1,
      triggerOnce: true 
    });
    
    useEffect(() => {
      if (isInView) {
        controls.start("visible");
        // Start with first milestone active
        setTimeout(() => setActiveIndex(0), 1500);
      }
    }, [controls, isInView]);
    
    const milestones = [
      // { 
      //   year: 2000, 
      //   title: "Real Estate Legacy Begins", 
      //   description: "Founded our real estate division in Himachal Pradesh, establishing it as a cornerstone of regional development for over two decades. Our inaugural projects set new standards for quality and reliability in the market.",
      //   icon: <Globe size={24} className="text-white" />,
      //   highlight: "Over 20 Years of Real Estate Leadership",
      //   shape: <Diamond className="absolute -bottom-8 -right-8 text-amber-400/20 dark:text-amber-400/10 w-32 h-32" />,
      //   color: "amber"
      // },
      { 
        year: 2005, 
        title: "Materials Division Launch", 
        description: "Expanded operations into Iron Rods Trading, strengthening our foundation in the construction materials sector and creating integrated solutions for our real estate clients.",
        icon: <Globe size={24} className="text-white" />,
         highlight: "Over 20 Years of Real Estate Leadership",
          shape: <Diamond className="absolute -bottom-8 -right-8 text-amber-400/20 dark:text-amber-400/10 w-32 h-32" />,
          color: "amber"
        },
    
      { 
        year: 2011, 
        title: "Premium Automotive Venture", 
        description: "Established our first SKODA dealership in Mandi, introducing European automotive luxury to Himachal Pradesh and diversifying our business portfolio beyond real estate and construction.",
        icon: <TrendingUp size={24} className="text-white" />,
        shape: <Triangle className="absolute -bottom-8 -right-8 text-blue-400/20 dark:text-blue-400/10 w-32 h-32" />,
        color: "blue"
      },
      { 
        year: 2014, 
        title: "Hyundai Strategic Alliance", 
        description: "Secured authorized dealership of Hyundai Motors India Ltd, expanding our automotive division and cementing our reputation as a trusted name in vehicle sales and service.",
        icon: <Users size={24} className="text-white" />,
        shape: <Hexagon className="absolute -bottom-10 -right-10 text-indigo-400/20 dark:text-indigo-400/10 w-36 h-36" />,
        color: "indigo"
      },
      { 
        year: 2019, 
        title: "Gulmohar Grand", 
        description: "The business entered the hospitality industry with the launch of The Gulmohar Grand, a premier hotel aimed at providing high-quality accommodation and services to both business and leisure travelers.",
        icon: <Users size={24} className="text-white" />,
        shape: <Hexagon className="absolute -bottom-10 -right-10 text-indigo-400/20 dark:text-indigo-400/10 w-36 h-36" />,
        color: "indigo"
      },
      { 
        year: 2021, 
        title: "Regional Market Expansion", 
        description: "Further expanded our automotive footprint with a new SKODA dealership in Solan, continuing our strategy of bringing premium services to underserved markets in Himachal Pradesh.",
        icon: <Home size={24} className="text-white" />,
        shape: <Diamond className="absolute -bottom-8 -right-8 text-violet-400/20 dark:text-violet-400/10 w-32 h-32" />,
        color: "violet"
      },
      { 
        year: 2025, 
        title: "Sustainable Energy Innovation", 
        description: "Launched Power FDL Solar, marking our entry into renewable energy solutions and aligning our business growth with environmental sustainability and the future needs of our region.",
        icon: <Sun size={24} className="text-white" />,
        highlight: "Leading Green Energy Solutions in Himachal Pradesh",
        shape: <Hexagon className="absolute -bottom-10 -right-10 text-teal-400/20 dark:text-teal-400/10 w-36 h-36" />,
        color: "teal"
      }
    ];
    
    const colorClasses = {
      amber: {
        primary: "text-amber-500 dark:text-amber-400",
        light: "text-amber-400 dark:text-amber-300",
        bg: "bg-amber-500 dark:bg-amber-600",
        bgLight: "bg-amber-400 dark:bg-amber-500",
        bgDark: "bg-amber-600 dark:bg-amber-700",
        borderLight: "border-amber-400/30 dark:border-amber-500/30",
        gradient: "from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700",
        gradientLight: "from-amber-400/20 to-amber-500/20 dark:from-amber-500/20 dark:to-amber-600/20"
      },
      emerald: {
        primary: "text-emerald-500 dark:text-emerald-400",
        light: "text-emerald-400 dark:text-emerald-300",
        bg: "bg-emerald-500 dark:bg-emerald-600",
        bgLight: "bg-emerald-400 dark:bg-emerald-500",
        bgDark: "bg-emerald-600 dark:bg-emerald-700",
        borderLight: "border-emerald-400/30 dark:border-emerald-500/30",
        gradient: "from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700",
        gradientLight: "from-emerald-400/20 to-emerald-500/20 dark:from-emerald-500/20 dark:to-emerald-600/20"
      },
      blue: {
        primary: "text-blue-500 dark:text-blue-400",
        light: "text-blue-400 dark:text-blue-300",
        bg: "bg-blue-500 dark:bg-blue-600",
        bgLight: "bg-blue-400 dark:bg-blue-500",
        bgDark: "bg-blue-600 dark:bg-blue-700",
        borderLight: "border-blue-400/30 dark:border-blue-500/30",
        gradient: "from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700",
        gradientLight: "from-blue-400/20 to-blue-500/20 dark:from-blue-500/20 dark:to-blue-600/20"
      },
      indigo: {
        primary: "text-indigo-500 dark:text-indigo-400",
        light: "text-indigo-400 dark:text-indigo-300",
        bg: "bg-indigo-500 dark:bg-indigo-600",
        bgLight: "bg-indigo-400 dark:bg-indigo-500",
        bgDark: "bg-indigo-600 dark:bg-indigo-700",
        borderLight: "border-indigo-400/30 dark:border-indigo-500/30",
        gradient: "from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700",
        gradientLight: "from-indigo-400/20 to-indigo-500/20 dark:from-indigo-500/20 dark:to-indigo-600/20"
      },
      violet: {
        primary: "text-violet-500 dark:text-violet-400",
        light: "text-violet-400 dark:text-violet-300",
        bg: "bg-violet-500 dark:bg-violet-600",
        bgLight: "bg-violet-400 dark:bg-violet-500",
        bgDark: "bg-violet-600 dark:bg-violet-700",
        borderLight: "border-violet-400/30 dark:border-violet-500/30",
        gradient: "from-violet-500 to-violet-600 dark:from-violet-600 dark:to-violet-700",
        gradientLight: "from-violet-400/20 to-violet-500/20 dark:from-violet-500/20 dark:to-violet-600/20"
      },
      teal: {
        primary: "text-teal-500 dark:text-teal-400",
        light: "text-teal-400 dark:text-teal-300",
        bg: "bg-teal-500 dark:bg-teal-600",
        bgLight: "bg-teal-400 dark:bg-teal-500",
        bgDark: "bg-teal-600 dark:bg-teal-700",
        borderLight: "border-teal-400/30 dark:border-teal-500/30",
        gradient: "from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700",
        gradientLight: "from-teal-400/20 to-teal-500/20 dark:from-teal-500/20 dark:to-teal-600/20"
      }
    };
    
    const getActiveColor = () => {
      if (activeIndex === null) return colorClasses.amber;
      return colorClasses[milestones[activeIndex].color];
    };
    
    return (
      <section className="py-12 overflow-hidden relative" ref={viewRef}>
        {/* 3D Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-800 dark:via-gray-900 dark:to-gray-950"></div>
        <div className="absolute top-0 right-0 w-full h-full overflow-hidden opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-400 dark:bg-amber-600 blur-3xl"></div>
          <div className="absolute top-1/2 -left-24 w-80 h-80 rounded-full bg-teal-400 dark:bg-teal-600 blur-3xl"></div>
          <div className="absolute -bottom-24 right-1/4 w-64 h-64 rounded-full bg-violet-400 dark:bg-violet-600 blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Header with 3D Effect */}
          <div className="text-center mb-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="relative"
            >
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 w-64 h-24 bg-amber-400/10 dark:bg-amber-600/10 rounded-full blur-3xl"></div>
              
              <div className="inline-flex items-center bg-white dark:bg-gray-800 px-6 py-1 rounded-full shadow-xl shadow-amber-100 dark:shadow-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 mb-8">
                <div className="w-5 h-5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 dark:from-amber-500 dark:to-amber-600 flex items-center justify-center mr-3 shadow-md shadow-amber-200 dark:shadow-amber-900/30">
                  <Calendar size={16} className="text-white" />
                </div>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-amber-800 dark:from-amber-400 dark:to-amber-600 font-bold tracking-wide">
                  20+ YEARS IN REAL ESTATE
                </span>
              </div>
              
              <h2 className="text-4xl md:text-5 xl font-bold text-gray-900 dark:text-gray-100 mb-6 tracking-tight">
                The Dev Bhoomi <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-500 to-amber-700 dark:from-amber-400 dark:to-amber-600">Legacy</span>
              </h2>
              
              <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg">
                From our foundation in real estate over two decades ago to our evolution into a diversified conglomerate spanning seven companies across four business verticals.
              </p>
            </motion.div>
          </div>
          
          {/* Interactive Timeline Interface */}
          <div className="relative pb-12">
            {/* Timeline Track */}
            <motion.div 
              className="absolute left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 top-16 rounded-full hidden md:block"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.5, delay: 0.5 }}
            ></motion.div>
            
            {/* Year Markers */}
            <div className="hidden md:flex justify-between mb-10 px-8 relative">
              {milestones.map((milestone, idx) => {
                const isActive = activeIndex === idx;
                const color = colorClasses[milestone.color];
                
                return (
                  <motion.div
                    key={idx}
                    className="relative z-20"
                    initial={{ opacity: 0, y: 20 }}
                    animate={controls}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: { 
                          duration: 0.5,
                          delay: 0.8 + idx * 0.1
                        }
                      }
                    }}
                  >
                    <button
                      className={`relative flex flex-col items-center`}
                      onClick={() => setActiveIndex(idx)}
                    >
                      <div className={`w-6 h-6 rounded-full ${isActive ? `${color.bg} shadow-lg shadow-${milestone.color}-200 dark:shadow-${milestone.color}-900/30` : 'bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600'} flex items-center justify-center mb-3 transition-all duration-300`}>
                        {isActive && (
                          <div className={`absolute -inset-2 ${color.bg}/20 rounded-full animate-ping opacity-70`}></div>
                        )}
                      </div>
                      <span className={`text-sm font-bold ${isActive ? color.primary : 'text-gray-500 dark:text-gray-400'}`}>{milestone.year}</span>
                    </button>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Mobile Year Selector */}
            <div className="md:hidden mb-12">
              <motion.div 
                className="flex overflow-x-auto pb-4 px-2 gap-4 snap-x"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
              >
                {milestones.map((milestone, idx) => {
                  const isActive = activeIndex === idx;
                  const color = colorClasses[milestone.color];
                  
                  return (
                    <button
                      key={idx}
                      className={`flex-shrink-0 py-2 px-6 rounded-full snap-start ${
                        isActive 
                          ? `bg-gradient-to-r ${color.gradient} text-white shadow-lg shadow-${milestone.color}-200 dark:shadow-${milestone.color}-900/30` 
                          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                      onClick={() => setActiveIndex(idx)}
                    >
                      <span className="text-sm font-bold">{milestone.year}</span>
                    </button>
                  );
                })}
              </motion.div>
            </div>
            
            {/* Content Display */}
            <AnimatePresence mode="wait">
              {activeIndex !== null && (
                <motion.div
                  key={`content-${activeIndex}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ 
                    duration: 0.5,
                    type: "spring",
                    stiffness: 100,
                    damping: 20
                  }}
                  className="mb-16"
                >
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    {/* 3D Card */}
                    <div className="relative perspective-1000">
                      <motion.div 
                        className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transform-gpu`}
                        initial={{ rotateY: -15, rotateX: 10 }}
                        animate={{ 
                          rotateY: [15, -5, 5, 0],
                          rotateX: [7, 3, 5, 0],
                          z: [0, 40, 20, 0]
                        }}
                        transition={{ 
                          duration: 2, 
                          ease: "easeOut",
                          times: [0, 0.3, 0.6, 1]
                        }}
                        style={{
                          transformStyle: "preserve-3d"
                        }}
                      >
                        <div className="p-8 relative z-10">
                          <div className="flex items-start justify-between mb-8">
                            <div>
                              <div className={`text-3xl font-bold ${colorClasses[milestones[activeIndex].color].primary} mb-4 tracking-tight`}>
                                {milestones[activeIndex].year}
                              </div>
                              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                                {milestones[activeIndex].title}
                              </h3>
                            </div>
                            
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${colorClasses[milestones[activeIndex].color].gradient} flex items-center justify-center shadow-lg shadow-${milestones[activeIndex].color}-200 dark:shadow-${milestones[activeIndex].color}-900/30`}>
                              {milestones[activeIndex].icon}
                            </div>
                          </div>
                          
                          {milestones[activeIndex].highlight && (
                            <div className={`inline-flex items-center bg-gradient-to-r ${colorClasses[milestones[activeIndex].color].gradientLight} px-4 py-2 rounded-full text-sm font-medium ${colorClasses[milestones[activeIndex].color].primary} border ${colorClasses[milestones[activeIndex].color].borderLight} mb-6`}>
                              <Award size={16} className="mr-2" />
                              <span>{milestones[activeIndex].highlight}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Background Shape */}
                        <div className="absolute inset-0 opacity-70 overflow-hidden">
                          {milestones[activeIndex].shape}
                        </div>
                        
                        {/* 3D Effects */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-white dark:from-gray-800/0 dark:via-gray-800/0 dark:to-gray-800 opacity-30 pointer-events-none"></div>
                        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/5 dark:from-black/20 to-transparent pointer-events-none"></div>
                      </motion.div>
                    </div>
                    
                    {/* Content Description */}
                    <div>
                      <motion.div 
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 relative overflow-hidden"
                        initial={{ x: 50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                      >
                        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${colorClasses[milestones[activeIndex].color].gradient}`}></div>
                        
                        <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-2">
                          {milestones[activeIndex].description}
                        </p>
                        
                        {/* Key Achievements */}
                        <div className="mt-2 grid grid-cols-2 gap-4">
                          {activeIndex === 0 && (
                            <>
                              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-2 mb-2 border border-gray-100 dark:border-gray-600">
                                <h4 className={`text-sm font-medium ${colorClasses[milestones[activeIndex].color].primary} mb-2`}>
                                  Real Estate Experience
                                </h4>
                                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">20+ Years</div>
                              </div>
                              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-2 mb-2 border border-gray-100 dark:border-gray-600">
                                <h4 className={`text-sm font-medium ${colorClasses[milestones[activeIndex].color].primary} mb-2`}>
                                  Regional Presence
                                </h4>
                                <div className="text-gray-700 dark:text-gray-300">Himachal Pradesh</div>
                              </div>
                            </>
                          )}
                          
                          {activeIndex === milestones.length - 1 && (
                            <>
                              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-2 mb-2 border border-gray-100 dark:border-gray-600">
                                <h4 className={`text-sm font-medium ${colorClasses[milestones[activeIndex].color].primary} mb-2`}>
                                  Innovation Focus
                                </h4>
                                <div className="text-gray-700 dark:text-gray-300">Renewable Energy</div>
                              </div>
                              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-2 mb-2 border border-gray-100 dark:border-gray-600">
                                <h4 className={`text-sm font-medium ${colorClasses[milestones[activeIndex].color].primary} mb-2`}>
                                  Business Impact
                                </h4>
                                <div className="text-gray-700 dark:text-gray-300">Sustainability</div>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Navigation Controls */}
                        <div className="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                          <button 
                            className={`flex items-center px-4 py-1 rounded-lg ${
                              activeIndex > 0 
                                ? `text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700` 
                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            }`}
                            onClick={() => activeIndex > 0 && setActiveIndex(activeIndex - 1)}
                            disabled={activeIndex === 0}
                          >
                            <ChevronRight size={20} className="transform rotate-180 mr-2" />
                            <span>Previous</span>
                          </button>
                          
                          <button 
                            className={`flex items-center px-4 py-1 rounded-lg ${
                              activeIndex < milestones.length - 1 
                                ? `bg-gradient-to-r ${colorClasses[milestones[activeIndex].color].gradient} text-white shadow-md shadow-${milestones[activeIndex].color}-200 dark:shadow-${milestones[activeIndex].color}-900/30` 
                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            }`}
                            onClick={() => activeIndex < milestones.length - 1 && setActiveIndex(activeIndex + 1)}
                            disabled={activeIndex === milestones.length - 1}
                          >
                            <span>Next</span>
                            <ChevronRight size={20} className="ml-2" />
                          </button>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Overall Stats Section */}
            <motion.div 
              className="mt-16"
              initial={{ opacity: 0, y: 40 }}
              animate={controls}
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { 
                  opacity: 1, 
                  y: 0,
                  transition: { 
                    duration: 0.8,
                    delay: 1.2
                  }
                }
              }}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 pt-6 text-center transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group overflow-hidden">
                  <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/50 flex items-center justify-center mx-auto mb-6 group-hover:bg-amber-100 dark:group-hover:bg-amber-800/50 transition-all duration-300">
                    <Calendar size={32} className="text-amber-500 dark:text-amber-400" />
                  </div>
                  <h4 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">20+</h4>
                  <p className="text-gray-500 dark:text-gray-400">Years in Real Estate</p>
                  <div className="absolute top-0 left-0 w-full h-1 bg-amber-500 dark:bg-amber-600"></div>
                  <div className="absolute top-4 right-4 rounded-full w-3 h-3 bg-amber-500 dark:bg-amber-600"></div>
                </div>
                
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 pt-6 text-center transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group overflow-hidden">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/50 flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-800/50 transition-all duration-300">
                    <Building size={32} className="text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <h4 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">7</h4>
                  <p className="text-gray-500 dark:text-gray-400">Companies</p>
                  <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500 dark:bg-emerald-600"></div>
                  <div className="absolute top-4 right-4 rounded-full w-3 h-3 bg-emerald-500 dark:bg-emerald-600"></div>
                </div>
                
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 pt-6 text-center transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group overflow-hidden">
                  <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/50 flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-100 dark:group-hover:bg-blue-800/50 transition-all duration-300">
                    <TrendingUp size={32} className="text-blue-500 dark:text-blue-400" />
                  </div>
                  <h4 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">4</h4>
                  <p className="text-gray-500 dark:text-gray-400">Business Verticals</p>
                  <div className="absolute top-0 left-0 w-full h-1 bg-blue-500 dark:bg-blue-600"></div>
                  <div className="absolute top-4 right-4 rounded-full w-3 h-3 bg-blue-500 dark:bg-blue-600"></div>
                </div>
                
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 pt-6 text-center transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group overflow-hidden">
                  <div className="w-10 h-10 rounded-full bg-violet-50 dark:bg-violet-900/50 flex items-center justify-center mx-auto mb-6 group-hover:bg-violet-100 dark:group-hover:bg-violet-800/50 transition-all duration-300">
                    <Award size={32} className="text-violet-500 dark:text-violet-400" />
                  </div>
                  <h4 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">1</h4>
                  <p className="text-gray-500 dark:text-gray-400">Vision for Excellence</p>
                  <div className="absolute top-0 left-0 w-full h-1 bg-violet-500 dark:bg-violet-600"></div>
                  <div className="absolute top-4 right-4 rounded-full w-3 h-3 bg-violet-500 dark:bg-violet-600"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* CSS for 3D Effects */}
        <style jsx>{`
          .perspective-1000 {
            perspective: 1000px;
          }
          
          @keyframes float {
            0% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(4deg); }
            100% { transform: translateY(0px) rotate(0deg); }
          }
          
          @keyframes pulse-ring {
            0% { transform: scale(0.8); opacity: 0.8; }
            70% { transform: scale(1.2); opacity: 0; }
            100% { transform: scale(0.8); opacity: 0; }
          }
        `}</style>
      </section>
    );
  };
export default AboutWidgetPremium;